import api from 'services/api';
import { REJECT, array, bool, nullable, numeric, object, str, union, type Received } from 'validation';
import { readResponse } from 'api/shared/readResponse';

// GET /locks/live — the whole Live Locks page in one read. Backend:
// routes/locks.js delegates to modules/generic_functions/locks/getLiveLocks.ts.
//
// Nothing here is shared with the other locks endpoints. The lock columns
// coincide with those of `GET /locks`, but this is its own statement
// aggregating holders and blocked instances, so the shapes are
// equal-by-coincidence and are declared in full (see ../../README.md).

/**
 * One flow a node instance runs inside. A flow whose node instances the caller
 * may not list keeps its place in the path, without its identity.
 */
export type LiveLockFlowStep =
  | {
      readonly redacted: false;
      readonly node_instance_id: number;
      readonly node_name: string | null;
    }
  | { readonly redacted: true };

/**
 * A node instance holding a lock, or blocked by one.
 *
 * One the caller may not list (judged on its node definition's zone) is still
 * listed, so a list agrees with its count, but carries only `status` and
 * `since`. The backend also sends its identity fields as null; nothing reads
 * them, so they are not declared.
 */
export type LiveLockInstance = {
  readonly status: string;
  /**
   * When the instance entered its current run of holding (or, when blocked,
   * waiting) statuses, from its status history. Null when the history has no
   * such entry.
   */
  readonly since: string | null;
} & (
  | {
      readonly redacted: false;
      readonly node_instance_id: number;
      readonly node_definition_id: number | null;
      readonly node_name: string | null;
      /** The flows the instance runs inside, outermost first; empty at top level. */
      readonly flow_path: readonly LiveLockFlowStep[];
    }
  | { readonly redacted: true }
);

export interface LiveLock {
  readonly lock_id: number;
  readonly name: string;
  readonly description: string | null;
  readonly zone_id: number;
  readonly zone_name: string;
  readonly limit: number;
  /**
   * Node instances in an active status that require the lock: the dispatcher's
   * own rule. Can exceed `limit` when the limit was lowered under running work.
   */
  readonly held_count: number;
  /** Node instances waiting on a lock that require this one while it is full. */
  readonly blocked_count: number;
  readonly holders: readonly LiveLockInstance[];
  /** The lock has more holders than `holders` carries; `held_count` is the true count. */
  readonly holders_truncated: boolean;
  readonly blocked: readonly LiveLockInstance[];
  /** The lock blocks more instances than `blocked` carries; `blocked_count` is the true count. */
  readonly blocked_truncated: boolean;
  /**
   * False for a lock returned only because `lock_ids` asked for it, or because
   * idle locks were asked for: nothing holds it and nothing waits on it.
   */
  readonly qualifies: boolean;
}

export interface LiveLocksResponse {
  readonly locks: readonly LiveLock[];
  /** The row cap was hit; the page must say so rather than imply completeness. */
  readonly truncated: boolean;
  readonly limit: number;
  /** The most holders, and separately the most blocked instances, one lock carries. */
  readonly instance_limit: number;
  /** Taken just before the read, so the data is no older than this. */
  readonly as_of: string;
}

const flowStepSchema = union<LiveLockFlowStep, 'redacted'>('redacted', {
  false: {
    node_instance_id: numeric(),
    node_name: nullable(str()),
  },
  true: {},
});

const instanceSchema = union<LiveLockInstance, 'redacted'>('redacted', {
  false: {
    status: str(),
    since: nullable(str()),
    // Addresses the row's link; an instance defaulted to id 0 would link to nothing.
    node_instance_id: numeric(REJECT),
    node_definition_id: nullable(numeric()),
    node_name: nullable(str()),
    flow_path: array(flowStepSchema),
  },
  true: {
    status: str(),
    since: nullable(str()),
  },
});

const liveLockSchema = object<LiveLock>({
  lock_id: numeric(REJECT),
  name: str(),
  description: nullable(str()),
  zone_id: numeric(),
  zone_name: str(),
  // The card is the claim "n of limit held, m blocked". A substituted 0 is a
  // confident lie about live state, so the row is not a row without them.
  limit: numeric(REJECT),
  held_count: numeric(REJECT),
  blocked_count: numeric(REJECT),
  holders: array(instanceSchema),
  holders_truncated: bool(),
  blocked: array(instanceSchema),
  blocked_truncated: bool(),
  qualifies: bool(),
});

const responseSchema = object<LiveLocksResponse>({
  locks: array(liveLockSchema),
  truncated: bool(),
  limit: numeric(),
  instance_limit: numeric(),
  as_of: str(),
});

export interface LiveLocksParams {
  readonly zone_id?: number;
  readonly search?: string;
  /** Also return locks that nothing holds and nothing waits on. */
  readonly include_idle?: boolean;
  /** Returned first, whether or not they qualify; bypasses zone, search and idle filters. */
  readonly lock_ids?: readonly number[];
}

/**
 * GET `/locks/live`. Throws on transport error only. A response that fails
 * validation is returned as an `unreadable` result rather than thrown, and the
 * caller decides what that costs (see `readResponse`).
 *
 * An enforced field drops the offending lock (or instance) rather than failing
 * the call: one untrustworthy card should not cost the operator the rest of
 * the page.
 */
export const getLiveLocks = async (params: LiveLocksParams = {}): Promise<Received<LiveLocksResponse>> => {
  const queryParams: Record<string, string | number> = {};
  if (params.zone_id !== undefined) {
    queryParams.zone_id = params.zone_id;
  }
  if (params.search) {
    queryParams.search = params.search;
  }
  if (params.include_idle) {
    queryParams.include_idle = 'true';
  }
  if (params.lock_ids?.length) {
    queryParams.lock_ids = params.lock_ids.join(',');
  }

  const response = await api.get('/locks/live', { params: queryParams });
  return readResponse(response.data, {
    schema: responseSchema,
    source: 'getLiveLocks',
  });
};
